import { computed } from 'vue'
import { listItems, useData } from '@bagelink/blox'
import { resolveAuthorForBlock, useCollection } from '@/composables/useDatastore'
import { isPublicRecord } from '@/utils/publication'
import portionsData from '@/data/portions.json'

/**
 * Build the "Torah Books" home-page data live from the datastore, instead of the
 * thin static `books-articles.json` seeded into the block props.
 *
 * Mapping: each `books` collection item carries `parashahIds` (the parashot that
 * belong to that book, in canonical order). Articles carry `parshaIds`. We match
 * an article to a book when the two sets intersect.
 *
 * The widget shows **one piece per parashah**, labelled with the parashah name
 * (not the book name), so Prev / Next walks the book portion by portion instead
 * of paging through an arbitrary slice of its articles.
 *
 * Portions per book come from the local `portions.json` (name + verses), which
 * is the canonical ordered list of the 54 parashot.
 */

interface BookRecord {
	name: string
	hebrewName?: string
	slug?: string
	order?: number
	parashahIds?: string[]
	bookArticleIds?: string[]
	_ssgArticles?: Array<Record<string, any>>
	_ssgHomepageArticles?: Array<Record<string, any>>
}

interface Portion {
	name: string
	verses: string
	book: string
}

export interface BookWidgetArticle {
	title: string
	authors: { name: string; title: string; image: string }[]
	image: string
	/** Display label above the title — the parashah name (book name as fallback). */
	category: string
	/** The parashah the piece belongs to, when it could be resolved. */
	parasha: string
	link: string
}

const BOOK_ORDER = ['Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy']

/** Stop scanning after this many articles when filling live (non-snapshot) builds. */
const LIVE_SCAN_LIMIT = 1500

/** Datastore values are usually arrays, but the `locale` serializer stringifies them. */
function asIdList(value: unknown): string[] {
	if (Array.isArray(value)) return value.filter((id): id is string => typeof id === 'string' && !!id)
	if (typeof value !== 'string' || !value.trim()) return []
	try {
		const parsed = JSON.parse(value.replace(/'/g, '"'))
		if (Array.isArray(parsed)) return parsed.filter((id): id is string => typeof id === 'string' && !!id)
	} catch {
		/* not an encoded list */
	}
	return []
}

function belongsToBook(parshaIds: string[], articleId: string, book: BookRecord) {
	return (book.bookArticleIds ?? []).includes(articleId)
		|| parshaIds.some(id => (book.parashahIds ?? []).includes(id))
}

export function useBooksWithArticles() {
	const { items: bookRecords, loading: booksLoading } = useCollection<BookRecord>('books', {
		limit: 20,
		pick: ['name', 'hebrewName', 'slug', 'order', 'parashahIds', 'bookArticleIds', '_ssgHomepageArticles'],
	})

	// Parashah records only supply the id → name/slug mapping used for labels.
	const parashaRecords = useCollection<{ _id?: string; id?: string; name?: string; slug?: string }>(
		'parashahs',
		{ limit: 100, pick: ['name', 'slug'] },
	)

	const parashaById = computed(() => {
		const map = new Map<string, { name: string; slug: string }>()
		for (const record of parashaRecords.items.value) {
			const id = record._id ?? record.id
			if (typeof id === 'string' && id && record.name) {
				map.set(id, { name: record.name, slug: record.slug ?? '' })
			}
		}
		return map
	})

	// Snapshot builds supply the compact (already one-per-parashah) articles.
	// Live datastore records do not. Scan pages only in that case, keeping the
	// first article found for each parashah of every book.
	const missingBooks = computed(() => bookRecords.value.filter(book => !Array.isArray(book._ssgHomepageArticles)))
	const { data: liveArticles, pending: articlesLoading } = useData<Record<string, any>[]>(
		() => `books:live-articles:v2:${JSON.stringify(missingBooks.value.map(book => [book.name, book.parashahIds, book.bookArticleIds]))}`,
		async () => {
			const missing = missingBooks.value
			if (!missing.length) return []
			const matches: Record<string, any>[] = []
			// Which parashot of each book already have their single piece.
			const covered = new Map(missing.map(book => [book.name, new Set<string>()]))
			const expected = new Map(missing.map(book => [book.name, (book.parashahIds ?? []).length]))
			const limit = 100
			let scanned = 0
			let offset = 0
			while (true) {
				const response = await listItems<Record<string, any>>('articles', { limit, offset })
				const page = Array.isArray(response) ? response : response.items ?? []
				for (const article of page) {
					if (!isPublicRecord('articles', article)) continue
					const parshaIds = asIdList(article.parshaIds)
					const articleId = String(article._id ?? article.id ?? '')
					let selected = false
					for (const book of missing) {
						const done = covered.get(book.name)!
						if (done.size >= (expected.get(book.name) ?? 0)) continue
						const parashah = (book.parashahIds ?? []).find(id => !done.has(id) && parshaIds.includes(id))
						if (!parashah && !(book.bookArticleIds ?? []).includes(articleId)) continue
						if (parashah) done.add(parashah)
						selected = true
					}
					if (selected) {
						matches.push({
							_id: article._id, id: article.id, slug: article.slug,
							title: article.title, image: article.image, link: article.link,
							parshaIds, authors: article.authors,
							authorIds: article.authorIds, authorId: article.authorId,
							secondAuthorId: article.secondAuthorId, thirdAuthorId: article.thirdAuthorId,
						})
					}
				}
				scanned += page.length
				const complete = missing.every(book => covered.get(book.name)!.size >= (expected.get(book.name) ?? 0))
				if (page.length < limit || complete || scanned >= LIVE_SCAN_LIMIT) break
				offset += page.length
			}
			return matches
		},
		{ default: [] },
	)

	const portions = portionsData as Portion[]

	// Keep one article per parashah, ordered canonically (the order the parashot
	// are read), and label each with its parashah name.
	function pickOnePerParashah(rows: Record<string, any>[], book: BookRecord, bookName: string) {
		const order = book.parashahIds ?? []
		const rank = new Map(order.map((id, index) => [id, index]))
		const seen = new Set<string>()
		const picked: Array<{
			row: Record<string, any>
			parasha: { name: string; slug: string } | null
			rank: number
		}> = []

		for (const row of rows) {
			const parshaIds = asIdList(row.parshaIds)
			let parasha: { name: string; slug: string } | null = null
			let parashaRank = order.length

			// The first parashah of this book the article is tagged with.
			for (const id of order) {
				if (!parshaIds.includes(id)) continue
				const record = parashaById.value.get(id)
				if (!record) continue
				parasha = record
				parashaRank = rank.get(id) ?? order.length
				break
			}

			const key = parasha ? `parasha:${parasha.name}` : `article:${row._id ?? row.id ?? row.title}`
			if (seen.has(key)) continue
			seen.add(key)
			picked.push({ row, parasha, rank: parashaRank })
		}

		picked.sort((a, b) => a.rank - b.rank)

		return picked.map(({ row, parasha }) => {
			const authorIds = asIdList(row.authorIds)
			const ids = authorIds.length
				? authorIds
				: [row.authorId, row.secondAuthorId, row.thirdAuthorId].filter(Boolean)
			return {
				title: row.title || '',
				authors: row.authors?.length ? row.authors : ids.map(resolveAuthorForBlock).filter(Boolean),
				image: row.image || '',
				category: parasha?.name || bookName,
				parasha: parasha?.name || '',
				link: row.link || `/article/${row.slug ?? ''}`,
			} satisfies BookWidgetArticle
		})
	}

	const books = computed(() => {
		const recs = bookRecords.value
		if (!recs.length) return []

		// Dedup book records by name (the collection can paginate duplicates) and
		// order them canonically (Genesis → Deuteronomy).
		const byName = new Map<string, BookRecord>()
		for (const r of recs) {
			if (r?.name && !byName.has(r.name)) byName.set(r.name, r)
		}

		return BOOK_ORDER.filter(name => byName.has(name)).map((name) => {
			const rec = byName.get(name)!

			const rows = rec._ssgHomepageArticles ?? liveArticles.value.filter(
				article => belongsToBook(
					asIdList(article.parshaIds),
					String(article._id ?? article.id ?? ''),
					rec,
				),
			)
			const bookArticles = pickOnePerParashah(rows, rec, name)

			const bookPortions = portions
				.filter(p => p.book === name)
				.map(p => ({ name: p.name, verses: p.verses }))

			return { name, articles: bookArticles, portions: bookPortions }
		})
	})

	const loading = computed(() => booksLoading.value || articlesLoading.value || parashaRecords.loading.value)

	return { books, loading }
}
